import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { GenerationCard } from '@gilding/shared';
import { STYLE_PROFILES, normalizeWalletAddress } from '@gilding/shared';
import { api } from '../api/client.js';
import { Logo } from '../components/Logo.js';
import { GoldLeafMark } from '../components/CreditBadge.js';
import { GildButton } from '../components/GildButton.js';
import { Reveal } from '../components/Reveal.js';
import { GildedImage } from '../components/GildedImage.js';
import { MoltenGold } from '../components/MoltenGold.js';
import { AccountActivity } from '../components/AccountActivity.js';
import { ProductHealth } from '../components/ProductHealth.js';

/**
 * Dashboard — inside-cover of the book.
 *
 * A single asymmetric column. The balance is the hero (illuminated number,
 * gold-leaf sheet mark, animated count-up). A LeaderboardPeek and FeedStrip follow
 * as editorial entry points — actual content, not nav tiles. Hairline rules
 * separate sections; no two blocks share a shape.
 */
export function Dashboard() {
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  const feedQuery = useQuery({
    queryKey: ['feed', 'dashboard-strip'],
    queryFn: () => api.feed(5, 0),
    staleTime: 60_000,
  });
  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', 'dashboard-peek'],
    queryFn: () => api.leaderboard(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!meQuery.isLoading && !meQuery.data?.authenticated) {
      navigate('/', { replace: true });
    }
  }, [meQuery.isLoading, meQuery.data?.authenticated, navigate]);

  const credits = meQuery.data?.credits ?? 0;
  const address = meQuery.data?.address ?? '';
  const feedItems = (feedQuery.data?.items ?? []) as GenerationCard[];
  const topEntry = leaderboardQuery.data?.entries?.[0];
  // The leaderboard endpoint returns addresses + scores. The richest visual
  // we can offer is the latest published gilding by the leader. Match by
  // address against the feed strip (case-insensitive, lazy best-effort).
  const winningPiece =
    topEntry &&
    feedItems.find(
      (it) => normalizeWalletAddress(it.ownerAddress) === normalizeWalletAddress(topEntry.address),
    );

  return (
    <div className="flex flex-col gap-12">
      {/* ---------- Inside cover: personal address ---------- */}
      <Reveal as="header" className="flex flex-col gap-3">
        <h1 className="heading-display">Welcome back to your folio.</h1>
        <p className="font-display italic text-lede text-ink-soft">
          Signed in as{' '}
          <span className="not-italic font-mono text-caption text-ink">
            {address ? `${address.slice(0, 6)}…${address.slice(-4)}` : ''}
          </span>
          . Pick up where the ink dried.
        </p>
      </Reveal>

      {/* ---------- The balance — hero block, illuminated ---------- */}
      <Reveal delay={110}>
        <BalanceBlock credits={credits} />
      </Reveal>

      {/* ---------- Hairline + editorial entry points ---------- */}
      <div className="gild-rule" aria-hidden />
      <AccountActivity address={address} />
      {meQuery.data?.tier === 'admin' && <ProductHealth />}

      {/* ---------- Leaderboard peek — visual entry to the rank table ---------- */}
      <Reveal delay={60}>
        <LeaderboardPeek
          loading={leaderboardQuery.isLoading}
          topAddress={topEntry?.address}
          topScore={topEntry?.score}
          winningThumbnail={winningPiece?.thumbnail}
          winningStyle={winningPiece?.stylePreset}
        />
      </Reveal>

      <div className="hairline" aria-hidden />

      {/* ---------- Feed strip — five-up horizontal scroller ---------- */}
      <Reveal delay={60}>
        <FeedStrip loading={feedQuery.isLoading} items={feedItems} />
      </Reveal>
    </div>
  );
}

/* ============================================================
   Purse — the hero. Not a card. A .paper block with an
   illuminated numeral, animated count-up, gold-leaf flake.
   ============================================================ */

interface BalanceBlockProps {
  credits: number;
}

function BalanceBlock({ credits }: BalanceBlockProps) {
  const display = useCountUp(credits);
  return (
    <section
      className="theme-dark relative isolate overflow-hidden rounded-md px-7 py-9 ring-1 ring-accent/25 shadow-[0_30px_80px_-42px_rgba(0,0,0,0.7)] md:px-10 md:py-12"
      aria-labelledby="balance-heading"
    >
      {/* Living molten gold beneath your balance — your leaves, rendered as the
          gold they are. Hover (or drag, on mobile) to reveal the light. */}
      <MoltenGold className="absolute inset-0" />
      {/* Legibility veil — a deep warm-black that lets the molten gold breathe in
          the open top-right and stays dark under every line of copy. The old
          transparent-centre scrim let the busy gold poke through and read as
          cloudy; this is a clean single wash + a gentle bottom darkening. No
          muddy mid-tone. pointer-events-none so the hover-reveal still works. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            'radial-gradient(150% 110% at 92% 2%, rgba(12,9,15,0.18) 0%, rgba(9,7,12,0.64) 44%, rgba(7,5,10,0.84) 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{ background: 'linear-gradient(to bottom, rgba(7,5,10,0) 32%, rgba(7,5,10,0.42) 100%)' }}
      />
      <span id="balance-heading" className="sr-only">
        Your balance
      </span>

      <div className="relative z-10 flex flex-col gap-7 [text-shadow:0_1px_10px_rgba(0,0,0,0.72)] md:flex-row md:items-end md:justify-between md:gap-10">
        {/* The number itself — illuminated. The gold-leaf mark sits as a
            denomination, paired left of the figure and centred to it. */}
        <div className="flex flex-col">
          <p className="font-display italic text-caption text-ink-soft">
            Your balance
          </p>
          <div className="mt-1 flex items-center gap-3.5">
            <GoldLeafMark className="h-9 w-9 shrink-0 md:h-11 md:w-11" />
            <div className="flex items-baseline gap-3">
              <span
                key={display}
                className="heading-hero animate-count-up leading-none tabular-nums text-ink"
              >
                {display}
              </span>
              <span className="font-display italic text-lede text-ink">
                leaves
              </span>
            </div>
          </div>
          <p className="mt-3 max-w-md font-display italic text-body text-ink">
            One leaf is one gilding. Two variations cost two leaves, and four
            cost three.
          </p>
        </div>

        {/* The two actions, weighted differently. */}
        <div className="flex flex-col items-start gap-3 md:items-end">
          <GildButton to="/new" className="shadow-[0_12px_32px_-12px_rgba(0,0,0,0.9)]">
            Start gilding
          </GildButton>
          <div className="flex items-center gap-3">
            <Link to="/buy" className="btn-ghost">
              Get more leaves
            </Link>
            <span className="hairline-v h-4" aria-hidden />
            <Link to="/gallery" className="btn-ghost">
              Open the folio
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * useCountUp — tweens an integer up to its target over ~600ms with an
 * ease-out curve. Respects prefers-reduced-motion (snaps instantly).
 */
function useCountUp(target: number, durationMs = 600): number {
  const [value, setValue] = useState<number>(target);
  const fromRef = useRef<number>(target);
  const startedRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || target === value) {
      setValue(target);
      return;
    }
    fromRef.current = value;
    startedRef.current = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startedRef.current) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased);
      setValue(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // `value` is intentionally not a dependency — it's the tween's moving
    // output; re-running on every frame would restart the animation.
  }, [target]);

  return value;
}

/* ============================================================
   LeaderboardPeek — the #1 piece becomes the visual entry to
   the table. Full-width on mobile, image left + meta right on
   desktop. A typographic link, hairline rule, not a nav card.
   ============================================================ */

interface LeaderboardPeekProps {
  loading: boolean;
  topAddress: string | undefined;
  topScore: number | undefined;
  winningThumbnail: string | undefined;
  winningStyle: GenerationCard['stylePreset'] | undefined;
}

function LeaderboardPeek({
  loading,
  topAddress,
  topScore,
  winningThumbnail,
  winningStyle,
}: LeaderboardPeekProps) {
  if (loading) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">This week at the top</h2>
        <p className="font-display italic text-body text-ink-subtle">
          Counting the votes…
        </p>
      </section>
    );
  }

  if (!topAddress) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">This week at the top</h2>
        <p className="font-display italic text-body text-ink-soft">
          No leader yet. Publish a gilding and the top spot is yours for the
          taking. Monday resets the count.
        </p>
        <Link to="/leaderboard" className="btn-ghost mt-1 self-start">
          See the ranks
          <span aria-hidden>→</span>
        </Link>
      </section>
    );
  }

  const handle = `${topAddress.slice(0, 6)}…${topAddress.slice(-4)}`;
  const styleLabel = winningStyle ? STYLE_PROFILES[winningStyle].label : null;

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="heading-section">This week at the top</h2>
        <Link to="/leaderboard" className="btn-ghost">
          See the ranks
          <span aria-hidden>→</span>
        </Link>
      </div>

      <Link
        to="/leaderboard"
        className="group grid grid-cols-1 gap-6 md:grid-cols-[180px_1fr] md:gap-8"
      >
        {/* Image plate — or a quiet placeholder if no published piece yet. */}
        {winningThumbnail ? (
          <div className="card-plate plate-lift aspect-square w-full max-w-[260px] overflow-hidden md:max-w-none">
            <GildedImage
              src={winningThumbnail}
              alt="leading gilding this week"
              className="block h-full w-full object-cover group-hover:scale-[1.02]"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="card-plate flex aspect-square w-full max-w-[260px] items-center justify-center text-ink-subtle md:max-w-none">
            <Logo className="h-12 w-12 opacity-60" />
          </div>
        )}

        <div className="flex flex-col justify-center gap-3">
          <p className="font-display italic text-caption text-ink-subtle">
            Leading this week
          </p>
          <p className="heading-section text-ink">{handle}</p>
          <div className="flex items-center gap-3 text-ink-soft">
            <span className="font-display italic text-lede">
              {topScore ?? 0} upvote{(topScore ?? 0) === 1 ? '' : 's'}
            </span>
            {styleLabel && (
              <>
                <span className="hairline-v h-4" aria-hidden />
                <span className="tag">{styleLabel}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </section>
  );
}

/* ============================================================
   FeedStrip — five-up horizontal scroller of the latest public
   gildings. Snap-aligned on touch, hairline-thin chrome.
   ============================================================ */

interface FeedStripProps {
  loading: boolean;
  items: GenerationCard[];
}

function FeedStrip({ loading, items }: FeedStripProps) {
  // Two columns on phones, four on desktop. Hooks run before the early returns
  // below so the order is stable across loading / empty / loaded states.
  const [cols, setCols] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 768 ? 4 : 2,
  );
  useEffect(() => {
    const compute = (): void => setCols(window.innerWidth >= 768 ? 4 : 2);
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  if (loading) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">Fresh off the press</h2>
        <p className="font-display italic text-body text-ink-subtle">
          Drying the ink…
        </p>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">Fresh off the press</h2>
        <p className="font-display italic text-body text-ink-soft">
          No public gildings yet today. The first published piece lands here.
        </p>
        <Link to="/feed" className="btn-ghost mt-1 self-start">
          Open the feed
          <span aria-hidden>→</span>
        </Link>
      </section>
    );
  }

  // Assign the four newest to fixed columns by `i % cols`, so the newest still
  // reads left-to-right across the TOP row, but each column is an independent
  // vertical stack.
  const shown = items.slice(0, 4);
  const pressColumns: Array<Array<{ it: GenerationCard; i: number }>> = Array.from(
    { length: cols },
    () => [],
  );
  shown.forEach((it, i) => pressColumns[i % cols]?.push({ it, i }));

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="heading-section">Fresh off the press</h2>
        <Link to="/feed" className="btn-ghost">
          See the feed
          <span aria-hidden>→</span>
        </Link>
      </div>

      {/* Masonry columns, not a grid. A grid row is sized to its TALLEST cell,
          so a short landscape tile left an empty band beneath it next to a
          portrait. Independent column stacks pack flush instead, while the
          `auto ratio` placeholder still reserves each height so plates stay put
          as images load. */}
      <div className="flex items-start gap-5">
        {pressColumns.map((col, ci) => (
          <div key={ci} className="flex min-w-0 flex-1 flex-col gap-5">
            {col.map(({ it, i }) => {
              const styleLabel = STYLE_PROFILES[it.stylePreset]?.label ?? it.stylePreset;
              const handle = `${it.ownerAddress.slice(0, 6)}…${it.ownerAddress.slice(-4)}`;
              return (
                <Reveal as="div" key={it.id} delay={i * 70}>
                  <Link to="/feed" className="group flex flex-col gap-2">
                    <div className="card-plate plate-lift overflow-hidden">
                      <GildedImage
                        src={it.thumbnail}
                        alt="recent public gilding"
                        className="block h-auto w-full group-hover:scale-[1.03]"
                        style={{ aspectRatio: `auto ${it.aspectRatio.replace(':', ' / ')}` }}
                        loading="lazy"
                      />
                    </div>
                    <p className="tag truncate">{styleLabel}</p>
                    <p className="font-mono text-caption text-ink-subtle">{handle}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}
